/**
 * printDevice — terminal side of the print-device claim contract.
 *
 * The POS terminal can act as a "print receiver" (kind: 'terminal'): paired
 * once via a code entered in Print Center, then it claims jobs from
 * /api/print-receivers/* using an opaque per-device token sent as
 * X-Receiver-Token (NOT the terminal's Bearer JWT). The token never expires
 * on its own — it dies by admin Disable, 30 days of silence, or a local
 * "Disable on this terminal" (which just wipes it here).
 *
 * See backend/routes/print_receivers.py for the authoritative contract.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND_URL}/api/print-receivers`;

// localStorage keys (terminal-local)
const DEVICE_KEY = 'agrismart_print_device';         // {token, name, branch_name}
const AUTO_KEY = 'agrismart_print_device_auto';      // '1' = auto print on arrival
const LEDGER_KEY = 'agrismart_print_device_ledger';  // last printed job ids
const LEDGER_MAX = 200;

// ── Stored pairing ───────────────────────────────────────────────────────────

export function getStoredDevice() {
  try {
    const dev = JSON.parse(localStorage.getItem(DEVICE_KEY) || 'null');
    return dev?.token ? dev : null;
  } catch { return null; }
}

export function storeDevice(device) {
  try { localStorage.setItem(DEVICE_KEY, JSON.stringify(device)); } catch { /* private mode */ }
}

export function wipeDevice() {
  try { localStorage.removeItem(DEVICE_KEY); } catch { /* ignore */ }
}

// ── Auto Print toggle (terminal-local, not a server setting) ────────────────

export function getAutoPrint() {
  try { return localStorage.getItem(AUTO_KEY) === '1'; } catch { return false; }
}

export function setAutoPrint(on) {
  try { localStorage.setItem(AUTO_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}

// ── Double-print ledger ─────────────────────────────────────────────────────
// Crash-between-print-and-ack protection: if the terminal printed a job but
// died before POSTing /done, the server re-releases the job; on re-claim we
// find its id here and ack WITHOUT printing again. Last ~200 ids kept.

export function ledgerHas(jobId) {
  try { return JSON.parse(localStorage.getItem(LEDGER_KEY) || '[]').includes(jobId); }
  catch { return false; }
}

export function ledgerAdd(jobId) {
  try {
    const ids = JSON.parse(localStorage.getItem(LEDGER_KEY) || '[]').filter(id => id !== jobId);
    ids.push(jobId);
    localStorage.setItem(LEDGER_KEY, JSON.stringify(ids.slice(-LEDGER_MAX)));
  } catch { /* ignore */ }
}

// ── API calls ───────────────────────────────────────────────────────────────

const deviceHeaders = (token) => ({ headers: { 'X-Receiver-Token': token } });

/** No auth — returns {code, expires_in}; admin enters it in Print Center. */
export const generatePairingCode = () =>
  axios.post(`${BASE}/generate-code`, { kind: 'terminal' }).then(r => r.data);

/** No auth — {status}; on 'paired' carries token + receiver EXACTLY ONCE. */
export const pollPairing = (code) =>
  axios.get(`${BASE}/poll/${encodeURIComponent(code)}`).then(r => r.data);

export const heartbeat = (token, capabilities, appVersion) =>
  axios.post(`${BASE}/heartbeat`, { capabilities, app_version: appVersion }, deviceHeaders(token)).then(r => r.data);

/** THE polling endpoint — atomically claims pending jobs for this device. */
export const claimJobs = (token) =>
  axios.post(`${BASE}/jobs/claim`, {}, deviceHeaders(token)).then(r => r.data.jobs || []);

export const jobDone = (token, jobId) =>
  axios.post(`${BASE}/jobs/${jobId}/done`, {}, deviceHeaders(token));

export const jobFailed = (token, jobId, errorMessage) =>
  axios.post(`${BASE}/jobs/${jobId}/failed`, { error_message: errorMessage }, deviceHeaders(token));

/** Lease keep-alive while a claimed job is queued/printing (fire-and-forget). */
export const jobProgress = (token, jobId) =>
  axios.post(`${BASE}/jobs/${jobId}/progress`, {}, deviceHeaders(token));

/** Pull {document_type, document, business_info, doc_code} for local rendering. */
export const fetchRenderData = (token, job) =>
  axios.get(`${BASE}/render-data`, {
    params: {
      document_type: job.document_type || '',
      document_id: job.document_id || '',
      doc_code: job.doc_code || '',
    },
    ...deviceHeaders(token),
  }).then(r => r.data);
